import React, { useState, useEffect, useCallback } from 'react';
import { func } from 'prop-types';
import { Box, Center, Heading, Image } from '@chakra-ui/react';
import { useImaginationRoom } from '@invitato/imagination-room';

import { useGuest } from '@/context/guest';

import useInvitation from '@/hooks/useInvitation';
import useLang from '@/hooks/useLang';

import WithAnimation from '@/components/Common/WithAnimation';
import playMusic from '@/components/Common/FloatingMusic/utils/playMusic';
import ScrollToDown from '@/components/Common/ScrollToDown/V1';
import LazyImage from '@/components/Common/LazyImage';

import { BUTTON_PADDING_PROPS } from '@/constants/colors';
import { THE_BRIDE } from '@/constants';
import {
  ASSETS_BG_PATTERN,
  ASSETS_GEBYOK,
  ASSETS_FLOWER_1,
  ASSETS_FLOWER_2,
  ASSETS_WAYANG_SHINTA,
  ASSETS_WAYANG_RAMA,
  ASSETS_PURPLE_TREE,
  ASSETS_BUTTERFLY,
  ASSETS_GUNUNGAN,
  IMG_LOGO,
} from '@/constants/assets';
import { ENABLE_WAYANG } from '@/constants/feature-flags';
import { TEXT_PROPS } from '@/constants/default-props';

import txt from './locales';

/**
 * Function to render Component CoverV3
 * This component will be dissapear after user click show `Undangan`
 * @return {JSX.Element}
 }
 */
function Cover({ onSeeDetail, showDetailContent }) {
  const { isLoading, isCompleted } = useGuest();
  const [loading, setLoading] = useState(false);

  const { onGetFinalName } = useImaginationRoom();
  const isInvitation = useInvitation();
  const lang = useLang();

  const handleLoadingComplete = useCallback(() => {
    onSeeDetail();
    setLoading(false);
  }, [onSeeDetail]);

  const handleClickDetail = () => {
    if (loading) return;

    playMusic();
    setLoading(true);
  };

  useEffect(() => {
    let intervalGuest;

    // side-effect to show next page after guest data ready
    // @important we use interval to make sure guest data is ready first before we go to next section
    if (loading) {
      intervalGuest = setInterval(() => {
        if (!isLoading && isCompleted) {
          clearInterval(intervalGuest);
          handleLoadingComplete();
        }
      }, 1000);
    }

    // Force unmount
    return () => {
      if (intervalGuest) {
        clearInterval(intervalGuest);
      }
    };
  }, [loading, isLoading, isCompleted, isInvitation, handleLoadingComplete]);
  return (
    <WithAnimation top collapse when={!showDetailContent}>
      <Box
        minHeight="100vh"
        bgImage={ASSETS_BG_PATTERN}
        bgSize="cover"
        bgPos="top center"
        position="relative"
        overflow="hidden"
      >
        <Center
          flexDir="column"
          bgColor="rgb(107 91 73 / 58%)"
          minHeight="100vh"
          width="100%"
          position="relative"
        >
          <Box width="97%" mx="auto" pos="relative" zIndex="1">
            <WithAnimation delay={1250}>
              <Image src={ASSETS_GEBYOK} mt="60px" />
            </WithAnimation>
          </Box>

          {/* LOGO */}
          <Box position="absolute" top="36px" left="50%" transform="translateX(-50%)" zIndex="2">
            <WithAnimation top delay={1500}>
              {IMG_LOGO && <LazyImage src={IMG_LOGO} maxW="90px" mx="auto" />}
            </WithAnimation>
          </Box>

          {/* GUNUNGAN  */}
          <Box
            className="animation__flower"
            position="absolute"
            top={{ '3sm': '24px', '2sm': '90px', '1sm': '24px', sm: '70px' }}
            left={{ '3sm': '-70px', '2sm': '-80px', sm: '-70px' }}
            zIndex="2"
          >
            <WithAnimation left delay={1500}>
              <Image
                src={ASSETS_GUNUNGAN}
                width={{ '3sm': '300px', '2sm': '290px', sm: '250px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>
          <Box
            className="animation__flower-invert"
            position="absolute"
            top={{ '3sm': '24px', '2sm': '90px', '1sm': '24px', sm: '70px' }}
            right={{ '3sm': '-70px', '2sm': '-80px', sm: '-70px' }}
            zIndex="2"
          >
            <WithAnimation right delay={1500}>
              <Image
                src={ASSETS_GUNUNGAN}
                width={{ '3sm': '300px', '2sm': '290px', sm: '250px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>

          {/* TREES  */}
          <Box
            position="absolute"
            bottom={{ '3sm': '95px', '2sm': '175px', '1sm': '160px', sm: '80px' }}
            left={{ '3sm': '-160px', '2sm': '-160px', sm: '-165px' }}
            zIndex="2"
          >
            <WithAnimation left delay={1250}>
              <Image
                src={ASSETS_PURPLE_TREE}
                width={{ '3sm': '360px', '2sm': '320px', sm: '320px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>
          <Box
            position="absolute"
            bottom={{ '3sm': '95px', '2sm': '175px', '1sm': '160px', sm: '80px' }}
            right={{ '3sm': '-160px', '2sm': '-160px', sm: '-165px' }}
            zIndex="2"
          >
            <WithAnimation right delay={1250}>
              <Image
                src={ASSETS_PURPLE_TREE}
                width={{ '3sm': '360px', '2sm': '320px', sm: '320px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>

          {/* WAYANG  */}
          {ENABLE_WAYANG && (
            <>
              <Box
                className="animation-left-and-right"
                maxWidth="200px"
                position="absolute"
                bottom="95px"
                left={{ '3sm': '-50px', '2sm': '-80px', sm: '-65px' }}
                zIndex="3"
              >
                <WithAnimation left delay={1000}>
                  <Image
                    src={ASSETS_WAYANG_SHINTA}
                    width={{ '3sm': '180px', '2sm': '200px', sm: '170px' }}
                    transform="rotate(0deg)"
                  />
                </WithAnimation>
              </Box>
              <Box
                className="animation-right-and-left"
                maxWidth="200px"
                position="absolute"
                bottom="75px"
                right={{ '3sm': '-50px', '2sm': '-80px', sm: '-80px' }}
                zIndex="3"
              >
                <WithAnimation right delay={1000}>
                  <Image
                    src={ASSETS_WAYANG_RAMA}
                    width={{ '3sm': '180px', '2sm': '200px', sm: '170px' }}
                    transform="rotate(0deg)"
                  />
                </WithAnimation>
              </Box>
            </>
          )}

          {/* FLOWERS  */}
          <Box
            className="animation__flower"
            position="absolute"
            bottom="-60px"
            left="-50px"
            zIndex="3"
          >
            <WithAnimation left>
              <Image
                src={ASSETS_FLOWER_1}
                width={{ '3sm': '160px', '2sm': '180px', sm: '150px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>

          <Box
            className="animation__flower"
            position="absolute"
            bottom="-30px"
            left="40px"
            zIndex="2"
          >
            <WithAnimation bottom>
              <Image
                src={ASSETS_FLOWER_2}
                width={{ '3sm': '220px', '2sm': '260px', sm: '200px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>

          <Box
            className="animation__flower-invert"
            position="absolute"
            bottom="-50px"
            left="210px"
            zIndex="3"
          >
            <WithAnimation bottom>
              <Image
                src={ASSETS_FLOWER_2}
                width={{ '3sm': '220px', '2sm': '230px', sm: '200px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>

          <Box
            className="animation__flower-invert"
            position="absolute"
            bottom="-80px"
            right="-80px"
            zIndex="3"
          >
            <WithAnimation bottom>
              <Image
                src={ASSETS_FLOWER_1}
                width={{ '3sm': '210px', '2sm': '230px', sm: '190px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>

          {/* BRIDE SECTION */}
          <Box width="95%" mx="auto" pos="relative" zIndex="3">
            <Center
              flexDir="column"
              width="100%"
              pos="absolute"
              bottom={{ '3sm': '100px', '2sm': '170px', sm: '90px' }}
              fontSize="16px"
              px="8px"
              pb="40px"
            >
              <WithAnimation delay={1100}>
                <Heading
                  {...TEXT_PROPS.subHeading}
                  color="alternativeColorText"
                  margin="0 auto"
                  textAlign="center"
                  position="relative"
                  fontWeight="bold"
                  dangerouslySetInnerHTML={{
                    __html: onGetFinalName('coupleName', THE_BRIDE)
                  }}
                />
              </WithAnimation>
              <Box
                className="animation__butterfly"
                position="absolute"
                top={{ '3sm': '-50px', '2sm': '-50px', '1sm': '-50px', sm: '-50px' }}
                left={{ '3sm': '100px', '2sm': '70px', '1sm': '70px', sm: '50px' }}
                zIndex="3"
              >
                <WithAnimation bottom delay={1200}>
                  <Image src={ASSETS_BUTTERFLY} maxW="50px" />
                </WithAnimation>
              </Box>
              <WithAnimation delay={1200}>
                <ScrollToDown
                  loading={loading}
                  text={isInvitation ? txt.invitation[lang] : txt.announcement[lang]}
                  marginTop="8px"
                  {...BUTTON_PADDING_PROPS}
                  onClick={handleClickDetail}
                />
              </WithAnimation>
            </Center>
          </Box>
        </Center>
      </Box>
    </WithAnimation>
  );
}

Cover.propTypes = {
  onSeeDetail: func.isRequired,
};

export default Cover;
